import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Avatar,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useDebounce from "hooks/useDebounce";
import useUsers from "hooks/useUsers";
import { nanoid } from "nanoid";
import * as React from "react";
import { useState } from "react";
import {
  IFilterPagingParams,
  ITeamMember,
  IUsers,
  MemberRoleEnum,
  MemberTeamManagementRoleEnum,
} from "utils/api.interfaces";

interface Props {
  onMemberSelected: (
    member: ITeamMember,
    roles: MemberRoleEnum[],
    isAdmin: boolean
  ) => void;
  teamId: string;
}

const AddTeamMember: React.FC<Props> = ({ onMemberSelected, teamId }) => {
  const [selectedMember, setSelectedMember] =
    React.useState<ITeamMember | null>(null);
  const [selectedRoles, setSelectedRoles] = React.useState<MemberRoleEnum[]>(
    []
  );
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string>("");
  const [filterInputLoader, setFilterInputLoader] =
    React.useState<boolean>(false);
  const debouncedValue = useDebounce<string>(filterValue, 500);

  const initialSearchParams: IFilterPagingParams = {
    page: "0",
    filter: "",
    pageSize: "50",
  };

  const { isLoading, data } = useUsers({
    filter: filterValue,
    page: initialSearchParams.page,
    pageSize: initialSearchParams.pageSize,
  }, {
    refetchOnReconnect: "always",
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  React.useEffect(() => {
    setFilterInputLoader(false);
  }, [debouncedValue]);

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(event.target.value);
    setFilterInputLoader(true);
  };

  const mapUserToTeamMember = (user: IUsers): ITeamMember => ({
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    imageUrl: user.imageUrl || user.profileImageUrl,
    email: user.email,
    teamManagementRoles: [],
    rolesData: [],
    dateOfBirthUtc: new Date().toISOString(),
    weightInKg: 0,
    heightInCm: 0,
  });

  const handleMemberSelect = (user: IUsers) => {
    const teamMember = mapUserToTeamMember(user);
    setSelectedMember(teamMember);
    setSelectedRoles([MemberRoleEnum.player]); // Default to player role
    setIsAdmin(false);
  };

  const handleRoleChange = (role: MemberRoleEnum, checked: boolean) => {
    if (checked) {
      setSelectedRoles((prev) => [...prev, role]);
    } else {
      setSelectedRoles((prev) => prev.filter((r) => r !== role));
    }
  };

  const handleTeamManagementRoleChange = (checked: boolean) => {
    setIsAdmin(checked);
    if (selectedMember) {
      setSelectedMember((prev) => ({
        ...prev!,
        teamManagementRoles: checked
          ? [MemberTeamManagementRoleEnum.admin]
          : [],
      }));
    }
  };

  const handleAddMember = () => {
    if (selectedMember) {
      const updatedMember = {
        ...selectedMember,
        teamManagementRoles: isAdmin
          ? [MemberTeamManagementRoleEnum.admin]
          : [],
      };
      onMemberSelected(updatedMember, selectedRoles, isAdmin);
      // Reset form
      setSelectedMember(null);
      setSelectedRoles([]);
      setIsAdmin(false);
      setFilterValue("");
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Add New Team Member
        </Typography>
        <Stack direction="row" spacing={4}>
          {/* Left side - User Search */}
          <Stack spacing={2} sx={{ width: "50%" }}>
            <TextField
              margin="dense"
              id="filter"
              label="Search users"
              InputLabelProps={{
                shrink: true,
              }}
              variant="standard"
              onChange={handleChangeFilter}
              value={filterValue}
              sx={{ width: "100%" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="" edge="end">
                      {filterInputLoader ? (
                        <CircularProgress size={15} />
                      ) : (
                        <FilterListIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {filterValue && (
              <List
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                {data?.users.map((user) => (
                  <ListItem
                    key={nanoid(6)}
                    button
                    onClick={() => handleMemberSelect(user)}
                    selected={selectedMember?.id === user.id}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                      },
                      paddingTop: "1px",
                      paddingBottom: "1px",
                    }}
                  >
                    <ListItemIcon>
                      <Avatar
                        alt={`${user.firstName} ${user.lastName}`}
                        src={
                          user.imageUrl ? user.imageUrl : user.profileImageUrl
                        }
                        sx={{ width: 40, height: 40, marginRight: "10px" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${user.firstName} ${user.lastName}`}
                      secondary={user.email}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>

          {/* Right side - Member Form */}
          <Stack spacing={2} sx={{ width: "50%" }}>
            {selectedMember ? (
              <>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    alt={`${selectedMember.firstName} ${selectedMember.lastName}`}
                    src={
                      selectedMember.imageUrl
                        ? selectedMember.imageUrl
                        : selectedMember.profileImageUrl
                    }
                    sx={{ width: 64, height: 64 }}
                  />
                  <Stack>
                    <Typography variant="h6">
                      {selectedMember.firstName} {selectedMember.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedMember.email}
                    </Typography>
                  </Stack>
                </Stack>

                <FormControl component="fieldset">
                  <Typography variant="h4">Roles</Typography>
                  <FormGroup
                    sx={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      alignContent: "flex-start",
                    }}
                  >
                    {Object.values(MemberRoleEnum).map((role) => (
                      <FormControlLabel
                        key={role}
                        control={
                          <Checkbox
                            checked={selectedRoles.includes(role)}
                            onChange={(e) =>
                              handleRoleChange(role, e.target.checked)
                            }
                          />
                        }
                        label={role}
                      />
                    ))}
                  </FormGroup>
                </FormControl>

                <FormControl component="fieldset">
                  <Typography variant="h4">Team Management Roles</Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAdmin}
                        onChange={(e) =>
                          handleTeamManagementRoleChange(e.target.checked)
                        }
                      />
                    }
                    label="Admin"
                  />
                </FormControl>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddMember}
                  sx={{ mt: 2 }}
                >
                  Add Member
                </Button>
              </>
            ) : (
              <Stack
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ height: "100%" }}
              >
                <Typography variant="body1" color="text.secondary">
                  {filterValue
                    ? "Select a user from the search results"
                    : "Search for a user to add as team member"}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AddTeamMember;
